// src/components/EmailTemplateList.tsx
import { BreadCrumb } from 'components/BreadCrumb';
import DataTable, { Row } from 'components/DataTable';
import React, { useEffect, useState } from 'react';
import Modal from "components/Modal"; // Ensure Modal component is available
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiService from 'services/apiService';
import EmailTemplateDetails from './EmailTemplateDetails';
import DeletePopup from 'components/DeletePopup/deletePopup';
import EditEmailTemplate from './EditEmailTemplate';
// src/types/EmailTemplate.ts
export interface EmailTemplate {
    id: number;
    purpose: string;
    subject: string;
    message: string;
    message1: string;
    status: number;
  }
  
const EmailTemplateList: React.FC = () => {
  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [viewData, setViewData] = useState<EmailTemplate | null>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<EmailTemplate | null>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const BreadCrumbList = [
    { name: 'Email Templates', link: '/email_templates' },
    { name: 'List', link: '/email_templates' },
  ];

  useEffect(() => {
    fetchEmailTemplates();
  }, []);

  const fetchEmailTemplates = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/email_templates');
      console.log('Email Templates:', res.data);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching email templates:', err);
      toast.error('Failed to fetch email templates.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      await apiService.delete(`/admin/email_templates/${selectedRow.id}`);
      setData((prevData) => prevData.filter((item) => item.id !== selectedRow.id));
      setOpenDeletePopup(false);
      toast.success('Email template deleted successfully.');
    } catch (error) {
      console.error('Error deleting email template:', error);
      toast.error('Failed to delete email template.');
    }
  };

  const handleView = (row: EmailTemplate) => {
    setViewData(row);
    setOpenViewModal(true);
  };

  const handleEdit = (row: EmailTemplate) => {
    setViewData(row);
    setOpenEditModal(true);
  };

  const handleDeleteData = (row: EmailTemplate) => {
    setSelectedRow(row);
    setOpenDeletePopup(true);
  };

  const handleUpdate = () => {
    fetchEmailTemplates();
    toast.success('Email template updated successfully.');
  };

  const tableColumns = [
    { key: 'id', label: 'ID', bold: true, style: { width: '60px' } },
    { key: 'purpose', label: 'Purpose', bold: true },
    { key: 'subject', label: 'Subject' },
    { key: 'message', label: 'Message' },
    {
      key: 'status',
      label: 'Status',
      render: (value: any) => (
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
        key: "actions",
        label: "Actions",
        excludeExport: true,
        actions: (row:any) => (
          <div className="flex space-x-2">
            <button
              onClick={() => handleView(row)}
              className="text-blue-500 hover:text-blue-700"
              title="View Details"
            >
              <FaEye />
            </button>
            <button
            onClick={() => handleEdit(row)}
            className="text-green-500 hover:text-green-700"
            title="Edit Template"
          >
            <FaEdit />
          </button>
            <button
              onClick={() => handleDeleteData(row)}
              className="text-red-500 hover:text-red-700"
              title="Delete Enquiry"
            >
              <FaTrash />
            </button>
          </div>
        ),
      },
   
  ];

  return (
    <>
      <BreadCrumb BreadCrumbList={BreadCrumbList} />
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Email Templates List</h1>
        <DataTable data={data} columns={tableColumns} loading={loading} />
      </div>

      {/* View Email Template Modal */}
      {openViewModal && viewData && (
        <Modal isOpen={openViewModal} onClose={() => setOpenViewModal(false)} header="Email Template Details">
          <EmailTemplateDetails data={viewData} />
        </Modal>
      )}

      {/* Edit Email Template Modal */}
      {openEditModal && viewData && (
        <Modal isOpen={openEditModal} onClose={() => setOpenEditModal(false)} header="Edit Email Template">
          <EditEmailTemplate data={viewData} onClose={() => setOpenEditModal(false)} onUpdate={handleUpdate} />
        </Modal>
      )}

      {/* Delete Confirmation Popup */}
      {openDeletePopup && selectedRow && (
        <Modal isOpen={openDeletePopup} onClose={() => setOpenDeletePopup(false)} header="Confirm Deletion">
          <DeletePopup
            handleDelete={handleDelete}
            onClose={() => setOpenDeletePopup(false)}
            selectedRow={selectedRow}
          />
        </Modal>
      )}

      {/* Toast Container for Notifications */}
      <ToastContainer />
    </>
  );
};

export default EmailTemplateList;
